import { Box, type SxProps, TableCell, TableRow, type Theme } from "@mui/material";
import { type FC, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRevealWage } from "../../hooks/useRevealWage";
import { useScrollIntoViewOnce } from "../../hooks/useScrollIntoViewOnce";
import { SHADOW } from "../../theme/tokens";
import type { WageBreakdownResult } from "../../utils/models";
import { InlineHint } from "../UIToolkit/molecules/InlineHint";
import { type BreakdownPanel, BreakdownPanelProvider } from "./BreakdownPanelContext";
import { BreakdownStatusRow } from "./BreakdownStatusRow";
import { HomeDepartmentNote } from "./HomeDepartmentNote";
import { useBreakdownLinkAccess } from "./useBreakdownLinkAccess";
import { useCompositionFold } from "./useCompositionFold";
import { useLegacyTrainingLink } from "./useLegacyTrainingLink";
import { useMismatchReport } from "./useMismatchReport";
import { type BreakdownNurse, WageBreakdownFooterLinks } from "./WageBreakdownFooterLinks";
import { WageBreakdownHeader } from "./WageBreakdownHeader";
import { WageSegmentList } from "./WageSegmentList";
import { buildWageComposition } from "./wageComposition";

/**
 * The panel's card: a soft surface filling the columns it spans under its row (`columnCount`, up to the
 * actions column, as the breakdown always has), not capped narrower; flat and unbroken in print.
 */
const PANEL_CARD_SX: SxProps<Theme> = (theme) => ({
	mx: { xs: 1, sm: 2 },
	my: 1.25,
	backgroundColor: theme.palette.background.paper,
	border: `1px solid ${theme.palette.divider}`,
	borderRadius: 1,
	overflow: "hidden",
	boxShadow: SHADOW.PANEL_SOFT,
	"@media print": { boxShadow: "none", breakInside: "avoid" },
});

type WageBreakdownSubTableProps = {
	breakdown?: WageBreakdownResult;
	columnCount?: number;
	isLoading?: boolean;
	/** The fetch failed. Distinct from a successful empty breakdown, which is `breakdown` absent with this false. */
	isError?: boolean;
	onRetry?: () => void;
	/** The shift's nurse, for the header's name and role and the footer's "Edit <name>'s wage". */
	nurse?: BreakdownNurse;
	/** The shift's paid hours, unpaid breaks out — the figure the row's hours column shows, so the two agree. */
	paidHours: number;
	/** The nurse-in-shift id of the row the breakdown opened under: its links carry it, for the way back. */
	shiftId: string;
	/** Scroll the card into view once it renders — the user was just brought back to it. */
	autoScrollIntoView?: boolean;
	/** Start with the rule lines unfolded: the user was brought back to a breakdown they had left unfolded. */
	opensWithDetails: boolean;
};

/**
 * DAT-906 · A shift's wage breakdown, opened under its row: the total, then each segment of the shift with
 * the lines that built its rate — the base, every rule named exactly as configured with the scope that
 * made it match, overtime with its threshold, a break as a deduction — adding up to the cent. Every name
 * opens a card with the configuration rows behind it.
 */
export const WageBreakdownSubTable: FC<WageBreakdownSubTableProps> = ({
	breakdown,
	columnCount = 6,
	isLoading,
	isError,
	onRetry,
	nurse,
	paidHours,
	shiftId,
	autoScrollIntoView,
	opensWithDetails,
}) => {
	const { t } = useTranslation();
	const canViewWage = useRevealWage();
	const access = useBreakdownLinkAccess();
	const composition = useMemo(() => (breakdown ? buildWageComposition(breakdown) : undefined), [breakdown]);
	const fold = useCompositionFold(composition, opensWithDetails);
	useMismatchReport(composition?.mismatches);
	const legacyTrainingLink = useLegacyTrainingLink(composition, breakdown?.trainingStatus, access.canOpenWageSettings);
	const cardRef = useRef<HTMLDivElement>(null);
	useScrollIntoViewOnce(cardRef, autoScrollIntoView === true && composition !== undefined);
	const panel = useMemo<BreakdownPanel | undefined>(
		() =>
			breakdown && {
				shiftId,
				isActual: breakdown.isActual,
				detailsOpen: fold.open,
				isAgency: breakdown.payBasis === "agency",
				agencyName: breakdown.agencyName,
				canViewWage,
				access,
			},
		[breakdown, shiftId, fold.open, canViewWage, access],
	);

	if (isLoading) return <BreakdownStatusRow columnCount={columnCount} status="loading" />;
	if (isError) return <BreakdownStatusRow columnCount={columnCount} status="error" onRetry={onRetry} />;
	if (!breakdown || !composition || !panel) return null;

	return (
		<TableRow data-testid="wage-breakdown-subtable">
			<TableCell colSpan={columnCount} sx={{ p: 0, borderBottom: "none" }}>
				<BreakdownPanelProvider value={panel}>
					<Box ref={cardRef} sx={PANEL_CARD_SX}>
						<WageBreakdownHeader
							breakdown={breakdown}
							composition={composition}
							nurse={nurse}
							paidHours={paidHours}
							canViewWage={canViewWage}
							toggle={fold.collapsible ? { open: fold.open, onToggle: fold.toggle, controls: fold.controls } : undefined}
							legacyTrainingLink={legacyTrainingLink}
						/>
						{composition.hasRateTypeOnlyRows && (
							<InlineHint tone="neutral" dataTestId="wage-breakdown-legacy-note" sx={{ mx: 2, mt: 1.25 }}>
								{t("wageBreakdown.legacyNote")}
							</InlineHint>
						)}
						<WageSegmentList
							composition={composition}
							compositionOpen={fold.open}
							regionIdOf={fold.regionIdOf}
							agencyName={breakdown.agencyName}
							canViewWage={canViewWage}
							canOpenWageSettings={access.canOpenWageSettings}
						/>
						<HomeDepartmentNote composition={composition} />
						<WageBreakdownFooterLinks nurse={nurse} isAgency={panel.isAgency} access={access} />
					</Box>
				</BreakdownPanelProvider>
			</TableCell>
		</TableRow>
	);
};
